declare var FroalaEditor: any;

export interface FroalaListIndentCommandResult {
  handled: boolean;
  changed: boolean;
}

const listIndentLevelAttribute = 'data-blueprint-list-indent-level';
const blockIndentLevelAttribute = 'data-blueprint-block-indent-level';
const listIndentBindingKey = '__blueprintListIndentBinding';
const listIndentCommandPatchKey = '__blueprintListIndentCommandPatched';
const unorderedListStyleType = 'disc';
const indentableBlockSelector = 'p, div, h1, h2, h3, h4, h5, h6, blockquote';

type ListIndentCommand = 'indent' | 'outdent';

function toElementArray(collection: any): HTMLElement[] {
  if (!collection) {
    return [];
  }

  if (collection instanceof HTMLElement) {
    return [collection];
  }

  if (Array.isArray(collection)) {
    return collection.filter((item): item is HTMLElement => item instanceof HTMLElement);
  }

  if (typeof collection.toArray === 'function') {
    return collection.toArray().filter((item: any): item is HTMLElement => item instanceof HTMLElement);
  }

  if (typeof collection.length === 'number') {
    return Array.from({ length: collection.length })
      .map((_, index) => collection[index] || collection.get?.(index))
      .filter((item): item is HTMLElement => item instanceof HTMLElement);
  }

  return [];
}

export function registerFroalaListIndentCommand(): void {
  if (typeof FroalaEditor === 'undefined' || FroalaEditor[listIndentCommandPatchKey]) {
    return;
  }

  const originalIndentCommand = FroalaEditor.COMMANDS?.indent || {};
  const originalOutdentCommand = FroalaEditor.COMMANDS?.outdent || {};

  const indentCommand = {
    ...originalIndentCommand,
    title: originalIndentCommand.title || 'Increase Indent',
    refresh: function(this: any, button: any): void {
      refreshListIndentButton(this, button, 'indent');
    }
  };

  const outdentCommand = {
    ...originalOutdentCommand,
    title: originalOutdentCommand.title || 'Decrease Indent',
    refresh: function(this: any, button: any): void {
      refreshListIndentButton(this, button, 'outdent');
    }
  };

  if (typeof FroalaEditor.RegisterCommand === 'function') {
    FroalaEditor.RegisterCommand('indent', indentCommand);
    FroalaEditor.RegisterCommand('outdent', outdentCommand);
  }

  if (FroalaEditor.COMMANDS) {
    FroalaEditor.COMMANDS.indent = indentCommand;
    FroalaEditor.COMMANDS.outdent = outdentCommand;
  }

  FroalaEditor[listIndentCommandPatchKey] = true;
}

export function bindFroalaListIndentFix(editor: any): void {
  if (!editor || typeof editor !== 'object' || editor[listIndentBindingKey]) {
    return;
  }

  const editorRoot = getEditorRoot(editor);

  if (!editorRoot) {
    return;
  }

  editor[listIndentBindingKey] = true;

  const refreshAfterSelectionChange = () => {
    const refresh = () => {
      normalizeFroalaUnorderedListStyles(editor);
      refreshFroalaListIndentButtons(editor);
    };

    setTimeout(refresh, 0);
    setTimeout(refresh, 80);
    requestAnimationFrame?.(refresh);
  };

  editorRoot.addEventListener('mouseup', refreshAfterSelectionChange);
  editorRoot.addEventListener('keyup', refreshAfterSelectionChange);
  editorRoot.addEventListener('input', refreshAfterSelectionChange);
  editor.events?.on?.('contentChanged', refreshAfterSelectionChange);
  editor.events?.on?.('commands.after', refreshAfterSelectionChange);
  editor.events?.on?.('destroy', () => {
    editorRoot.removeEventListener('mouseup', refreshAfterSelectionChange);
    editorRoot.removeEventListener('keyup', refreshAfterSelectionChange);
    editorRoot.removeEventListener('input', refreshAfterSelectionChange);
    editor[listIndentBindingKey] = false;
  }, true);

  refreshAfterSelectionChange();
}

export function handleFroalaListIndentCommand(editor: any, command: string): FroalaListIndentCommandResult {
  if (command !== 'indent' && command !== 'outdent') {
    return { handled: false, changed: false };
  }

  const selectedListItems = getSelectedListItems(editor);

  if (!selectedListItems.length) {
    return { handled: false, changed: false };
  }

  const previousContent = editor.html?.get?.() || '';
  editor.undo?.saveStep?.();

  if (command === 'indent') {
    increaseListIndent(editor, selectedListItems);
  } else {
    decreaseListIndent(editor, selectedListItems);
  }

  normalizeFroalaUnorderedListStyles(editor);
  const changed = previousContent !== (editor.html?.get?.() || '');

  if (changed) {
    editor.undo?.saveStep?.();
  }

  refreshFroalaListIndentButtons(editor);

  return { handled: true, changed };
}

export function handleFroalaListBackspace(editor: any, event: KeyboardEvent): FroalaListIndentCommandResult {
  if (!isBackspaceEvent(event)) {
    return { handled: false, changed: false };
  }

  const selectedListItem = getCurrentListItem(editor);

  if (selectedListItem) {
    if (!isCollapsedAtStartOfElement(editor, selectedListItem) || !canDecreaseListIndent(editor, selectedListItem)) {
      return { handled: false, changed: false };
    }

    event.preventDefault();
    event.stopPropagation();

    return handleFroalaListIndentCommand(editor, 'outdent');
  }

  const selectedBlock = getCurrentIndentedBlock(editor);

  if (!selectedBlock || !isCollapsedAtStartOfElement(editor, selectedBlock) || !canDecreaseBlockIndent(editor, selectedBlock)) {
    return { handled: false, changed: false };
  }

  event.preventDefault();
  event.stopPropagation();

  return decreaseBlockIndent(editor, selectedBlock);
}

export function normalizeFroalaUnorderedListStyles(editor: any): void {
  const editorRoot = getEditorRoot(editor);

  if (!editorRoot) {
    return;
  }

  Array.from(editorRoot.querySelectorAll('ul')).forEach((list) => {
    (list as HTMLElement).style.setProperty('list-style-type', unorderedListStyleType, 'important');
  });
}

function increaseListIndent(editor: any, listItems: HTMLLIElement[]): void {
  const indentStep = getIndentStep(editor);

  listItems.forEach((listItem) => {
    const indentProperty = getIndentProperty(editor, listItem);
    const currentLevel = getListItemIndentLevel(listItem, indentStep, indentProperty);
    setListItemIndentLevel(listItem, currentLevel + 1, indentStep, indentProperty);
  });
}

function decreaseListIndent(editor: any, listItems: HTMLLIElement[]): void {
  const indentStep = getIndentStep(editor);

  [...listItems].reverse().forEach((listItem) => {
    const indentProperty = getIndentProperty(editor, listItem);
    const currentLevel = getListItemIndentLevel(listItem, indentStep, indentProperty);

    if (currentLevel > 0) {
      setListItemIndentLevel(listItem, currentLevel - 1, indentStep, indentProperty);
      return;
    }

    moveNestedListItemOut(listItem);
  });
}

function moveNestedListItemOut(listItem: HTMLLIElement): boolean {
  const parentList = listItem.parentElement as HTMLUListElement | null;
  const parentListItem = parentList?.parentElement instanceof HTMLLIElement ? parentList.parentElement : null;
  const targetList = parentListItem?.parentElement;

  if (!parentList || !parentListItem || !targetList) {
    return false;
  }

  targetList.insertBefore(listItem, parentListItem.nextSibling);

  if (!parentList.querySelector('li')) {
    parentList.remove();
  }

  return true;
}

function refreshFroalaListIndentButtons(editor: any): void {
  const selectedListItems = getSelectedListItems(editor);

  if (selectedListItems.length) {
    setToolbarButtonDisabled(editor, 'indent', false);
    setToolbarButtonDisabled(editor, 'outdent', !selectedListItems.some(listItem => canDecreaseListIndent(editor, listItem)));
  }

  const selectedBlock = getCurrentIndentedBlock(editor);

  if (selectedBlock) {
    setToolbarButtonDisabled(editor, 'outdent', !canDecreaseBlockIndent(editor, selectedBlock));
  }
}

function refreshListIndentButton(editor: any, button: any, command: ListIndentCommand): void {
  const selectedListItems = getSelectedListItems(editor);

  if (!selectedListItems.length) {
    const selectedBlock = getCurrentIndentedBlock(editor);

    if (selectedBlock && command === 'outdent') {
      setButtonDisabled(button, !canDecreaseBlockIndent(editor, selectedBlock));
    }

    return;
  }

  const disabled = command === 'outdent' && !selectedListItems.some(listItem => canDecreaseListIndent(editor, listItem));
  setButtonDisabled(button, disabled);
}

function setToolbarButtonDisabled(editor: any, command: ListIndentCommand, disabled: boolean): void {
  setButtonDisabled(editor?.$box?.find?.(`[data-cmd="${command}"]`), disabled);
  setButtonDisabled(editor?.$tb?.find?.(`[data-cmd="${command}"]`), disabled);
}

function setButtonDisabled(button: any, disabled: boolean): void {
  button?.toggleClass?.('fr-disabled', disabled);
  button?.attr?.('aria-disabled', String(disabled));

  if (disabled) {
    button?.attr?.('disabled', 'disabled');
  } else {
    button?.removeAttr?.('disabled');
  }
}

function canDecreaseListIndent(editor: any, listItem: HTMLLIElement): boolean {
  const indentProperty = getIndentProperty(editor, listItem);
  return getListItemIndentLevel(listItem, getIndentStep(editor), indentProperty) > 0 || isNestedListItem(listItem);
}

function getSelectedListItems(editor: any): HTMLLIElement[] {
  const editorRoot = getEditorRoot(editor);
  const selectedListItems: HTMLLIElement[] = [];

  const addListItem = (element: Element | null | undefined) => {
    const listItem = getListItemFromElement(element);

    if (listItem && (!editorRoot || editorRoot.contains(listItem)) && !selectedListItems.includes(listItem)) {
      selectedListItems.push(listItem);
    }
  };

  toElementArray(editor?.selection?.blocks?.()).forEach(addListItem);
  addListItem(editor?.selection?.element?.());

  const nativeSelection = getNativeSelection(editor);

  if (editorRoot && nativeSelection?.rangeCount) {
    const range = nativeSelection.getRangeAt(0);

    if (nativeSelection.isCollapsed) {
      addListItem(getElementFromNode(nativeSelection.anchorNode));
    } else {
      Array.from(editorRoot.querySelectorAll('li')).forEach((listItem) => {
        try {
          if (range.intersectsNode(listItem)) {
            addListItem(listItem);
          }
        } catch {
          // Ignore ranges from a different document.
        }
      });
    }
  }

  return selectedListItems.sort((firstItem, secondItem) => {
    return firstItem.compareDocumentPosition(secondItem) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1;
  });
}

function getCurrentListItem(editor: any): HTMLLIElement | null {
  const selectionElement = getElementFromNode(getNativeSelection(editor)?.anchorNode) || editor?.selection?.element?.();
  const listItem = getListItemFromElement(selectionElement);
  return listItem && isEditableListItem(listItem) ? listItem : null;
}

function getListItemFromElement(element: Element | null | undefined): HTMLLIElement | null {
  if (!element) {
    return null;
  }

  if (element instanceof HTMLLIElement) {
    return element;
  }

  return element.closest?.('li') as HTMLLIElement | null;
}

function isEditableListItem(listItem: HTMLLIElement): boolean {
  return ['UL', 'OL'].includes(listItem.parentElement?.tagName || '');
}

function isNestedListItem(listItem: HTMLLIElement): boolean {
  return listItem.parentElement?.parentElement instanceof HTMLLIElement &&
    ['UL', 'OL'].includes(listItem.parentElement.parentElement.parentElement?.tagName || '');
}

function getIndentStep(editor: any): number {
  const indentStep = Number(editor?.opts?.indentMargin);
  return Number.isFinite(indentStep) && indentStep > 0 ? indentStep : 20;
}

function getIndentProperty(editor: any, listItem: HTMLLIElement): 'margin-left' | 'margin-right' {
  const direction = editor?.opts?.direction || getComputedStyle(listItem).direction;
  return direction === 'rtl' ? 'margin-right' : 'margin-left';
}

function getListItemIndentLevel(listItem: HTMLLIElement, indentStep: number, indentProperty: 'margin-left' | 'margin-right'): number {
  const storedLevel = Number(listItem.getAttribute(listIndentLevelAttribute));

  if (Number.isFinite(storedLevel) && storedLevel > 0) {
    return storedLevel;
  }

  const currentMargin = parseFloat(listItem.style.getPropertyValue(indentProperty) || '0');
  return currentMargin > 0 ? Math.round(currentMargin / indentStep) : 0;
}

function setListItemIndentLevel(listItem: HTMLLIElement, level: number, indentStep: number, indentProperty: 'margin-left' | 'margin-right'): void {
  const normalizedLevel = Math.max(level, 0);
  const oppositeIndentProperty = indentProperty === 'margin-left' ? 'margin-right' : 'margin-left';

  if (normalizedLevel === 0) {
    listItem.removeAttribute(listIndentLevelAttribute);
    listItem.style.removeProperty(indentProperty);
    listItem.style.removeProperty(oppositeIndentProperty);
    return;
  }

  listItem.setAttribute(listIndentLevelAttribute, String(normalizedLevel));
  listItem.style.setProperty(indentProperty, `${normalizedLevel * indentStep}px`);
  listItem.style.removeProperty(oppositeIndentProperty);
}

function isCollapsedAtStartOfElement(editor: any, element: HTMLElement): boolean {
  const nativeSelection = getNativeSelection(editor);

  if (!nativeSelection?.rangeCount || !nativeSelection.isCollapsed) {
    return false;
  }

  const range = nativeSelection.getRangeAt(0);

  if (!element.contains(range.startContainer)) {
    return false;
  }

  const beforeCaretRange = range.cloneRange();
  beforeCaretRange.selectNodeContents(element);
  beforeCaretRange.setEnd(range.startContainer, range.startOffset);

  return beforeCaretRange.toString().replace(/\u200B/g, '').trim().length === 0;
}

function getCurrentIndentedBlock(editor: any): HTMLElement | null {
  const editorRoot = getEditorRoot(editor);

  if (!editorRoot) {
    return null;
  }

  const selectionElement = getElementFromNode(getNativeSelection(editor)?.anchorNode) || editor?.selection?.element?.();

  if (selectionElement?.closest?.('li')) {
    return null;
  }

  const selectedBlock = toElementArray(editor?.selection?.blocks?.())
    .find(block => isIndentableBlock(editorRoot, block));

  if (selectedBlock) {
    return selectedBlock;
  }

  const closestBlock = selectionElement?.closest?.(indentableBlockSelector) as HTMLElement | null;

  return closestBlock && isIndentableBlock(editorRoot, closestBlock) ? closestBlock : null;
}

function isIndentableBlock(editorRoot: HTMLElement, block: HTMLElement): boolean {
  return block.matches(indentableBlockSelector) &&
    block !== editorRoot &&
    editorRoot.contains(block) &&
    !block.closest('li') &&
    !block.classList.contains('fr-element') &&
    !block.classList.contains('fr-view');
}

function canDecreaseBlockIndent(editor: any, block: HTMLElement): boolean {
  const indentProperty = getBlockIndentProperty(editor, block);
  return getBlockIndentLevel(block, getIndentStep(editor), indentProperty) > 0;
}

function decreaseBlockIndent(editor: any, block: HTMLElement): FroalaListIndentCommandResult {
  const previousContent = editor.html?.get?.() || '';
  const indentStep = getIndentStep(editor);
  const indentProperty = getBlockIndentProperty(editor, block);
  const currentLevel = getBlockIndentLevel(block, indentStep, indentProperty);

  if (currentLevel <= 0) {
    return { handled: false, changed: false };
  }

  editor.undo?.saveStep?.();
  setBlockIndentLevel(block, currentLevel - 1, indentStep, indentProperty);

  const changed = previousContent !== (editor.html?.get?.() || '');

  if (changed) {
    editor.undo?.saveStep?.();
  }

  refreshFroalaListIndentButtons(editor);

  return { handled: true, changed };
}

function getBlockIndentProperty(editor: any, block: HTMLElement): 'margin-left' | 'margin-right' {
  const direction = editor?.opts?.direction || getComputedStyle(block).direction;
  return direction === 'rtl' ? 'margin-right' : 'margin-left';
}

function getBlockIndentLevel(block: HTMLElement, indentStep: number, indentProperty: 'margin-left' | 'margin-right'): number {
  const storedLevel = Number(block.getAttribute(blockIndentLevelAttribute));

  if (Number.isFinite(storedLevel) && storedLevel > 0) {
    return storedLevel;
  }

  const currentMargin = parseFloat(block.style.getPropertyValue(indentProperty) || '0');
  return currentMargin > 0 ? Math.round(currentMargin / indentStep) : 0;
}

function setBlockIndentLevel(block: HTMLElement, level: number, indentStep: number, indentProperty: 'margin-left' | 'margin-right'): void {
  const normalizedLevel = Math.max(level, 0);
  const oppositeIndentProperty = indentProperty === 'margin-left' ? 'margin-right' : 'margin-left';

  if (normalizedLevel === 0) {
    block.removeAttribute(blockIndentLevelAttribute);
    block.style.removeProperty(indentProperty);
    block.style.removeProperty(oppositeIndentProperty);
    return;
  }

  block.setAttribute(blockIndentLevelAttribute, String(normalizedLevel));
  block.style.setProperty(indentProperty, `${normalizedLevel * indentStep}px`);
  block.style.removeProperty(oppositeIndentProperty);
}

function isBackspaceEvent(event: KeyboardEvent): boolean {
  return event.key === 'Backspace' || event.keyCode === 8;
}

function getNativeSelection(editor: any): Selection | null {
  return editor?.win?.getSelection?.() || window.getSelection?.() || null;
}

function getElementFromNode(node: Node | null | undefined): HTMLElement | null {
  if (!node) {
    return null;
  }

  return node.nodeType === Node.ELEMENT_NODE ? node as HTMLElement : node.parentElement;
}

function getEditorRoot(editor: any): HTMLElement | null {
  return editor?.el instanceof HTMLElement ? editor.el : null;
}